/* eslint-disable implicit-arrow-linebreak */

/* eslint-disable import/no-cycle */
import dimensionsService from '@/services/dimensionsService';

const state = {
  dimensions: [], // Array of dimensions
  currentDimension: null, // Currently selected dimension for editing
  loading: false,
  error: null,

  // Import-specific state (isolated from main dimensions)
  importState: {
    analysis: null, // Current import analysis from DimensionMatchingService
    status: 'idle', // Import operation status: idle | analyzing | importing | success | error
    lastImportResult: null, // Import operation result for rollback
    canRollback: false, // Rollback availability
    rollbackData: null, // Data needed for rollback
  },
};

const getters = {
  /**
   * Get all dimensions
   */
  getAllDimensions: (storeState) => storeState.dimensions,

  /**
   * Get dimension by ID
   */
  getDimensionById: (storeState) => (id) =>
    storeState.dimensions.find((dimension) => dimension._id === id),

  /**
   * Get dimension by value
   */
  getDimensionByValue: (storeState) => (value) =>
    storeState.dimensions.find((dimension) => dimension.value === value),

  /**
   * Get dimensions by status (positive/negative result descriptions)
   */
  getDimensionsByStatus: (storeState) => (status) =>
    storeState.dimensions.filter(
      (dimension) =>
        dimension.resultDescriptions &&
        dimension.resultDescriptions.some((desc) => desc.status === status),
    ),

  /**
   * Search dimensions by term
   */
  searchDimensions: (storeState) => (searchTerm) => {
    if (!searchTerm) return storeState.dimensions;

    const searchLower = searchTerm.toLowerCase();
    return storeState.dimensions.filter(
      (dimension) =>
        dimension.value.toLowerCase().includes(searchLower) ||
        dimension.translation.en.toLowerCase().includes(searchLower) ||
        dimension.translation.de.toLowerCase().includes(searchLower) ||
        dimension.description.en.toLowerCase().includes(searchLower) ||
        dimension.description.de.toLowerCase().includes(searchLower),
    );
  },

  /**
   * Get loading state
   */
  isLoading: (storeState) => storeState.loading,

  /**
   * Get error state
   */
  getError: (storeState) => storeState.error,

  // Import-related getters
  /**
   * Check if import has conflicts
   */
  hasImportConflicts: (storeState) =>
    storeState.importState.analysis && storeState.importState.analysis.conflicts.length > 0,

  /**
   * Get import summary for display
   */
  importSummary: (storeState) => {
    const { analysis } = storeState.importState;
    if (!analysis) return null;

    const total = analysis.newDimensions.length +
      analysis.conflicts.length +
      analysis.duplicates.length;
    const newCount = analysis.newDimensions.length;
    const conflictsCount = analysis.conflicts.length;
    const duplicatesCount = analysis.duplicates.length;

    return {
      total,
      new: newCount,
      conflicts: conflictsCount,
      duplicates: duplicatesCount,
      willUpdate: conflictsCount,
    };
  },

  /**
   * Get current import status
   */
  importStatus: (storeState) => storeState.importState.status,

  /**
   * Check if rollback is available
   */
  canRollbackImport: (storeState) => storeState.importState.canRollback,

  /**
   * Get current import analysis
   */
  getImportAnalysis: (storeState) => storeState.importState.analysis,
};

const actions = {
  /**
   * Fetch all dimensions
   */
  async fetchDimensions({ commit, dispatch }) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.getAllDimensions();
      commit('SET_DIMENSIONS', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch dimensions',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch dimension by ID
   */
  async fetchDimensionById({ commit, dispatch }, dimensionId) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.getDimensionById(dimensionId);
      console.log('Backend response for dimension:', response.data);
      console.log('Questions in response:', response.data?.questions);
      console.log('Questions length in response:', response.data?.questions?.length);
      commit('SET_CURRENT_DIMENSION', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch dimension',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch dimension by value
   */
  async fetchDimensionByValue({ commit, dispatch }, value) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.getDimensionByValue(value);
      commit('SET_DIMENSION', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch dimension',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Save dimension (create or update)
   */
  async saveDimension({ commit, dispatch }, dimension) {
    commit('SET_LOADING', true);
    try {
      let savedDimension;
      if (!dimension._id) {
        // Create new dimension
        const response = await dimensionsService.createDimension(dimension);
        savedDimension = response.data;
        dispatch(
          'notifications/showToast',
          {
            detail: 'Dimension created successfully',
            severity: 'success',
          },
          { root: true },
        );
      } else {
        // Update existing dimension
        const dimensionToUpdate = { ...dimension };
        delete dimensionToUpdate._id;
        delete dimensionToUpdate.__v;

        const response = await dimensionsService.updateDimension(dimension._id, dimensionToUpdate);
        savedDimension = response.data;
        dispatch(
          'notifications/showToast',
          {
            detail: 'Dimension updated successfully',
            severity: 'success',
          },
          { root: true },
        );
      }
      // Update the store with the saved dimension
      commit('SET_DIMENSION', savedDimension);
      return savedDimension;
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to save dimension',
          severity: 'error',
        },
        { root: true },
      );
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete dimension
   */
  async deleteDimension({ commit, dispatch }, dimensionId) {
    commit('SET_LOADING', true);
    try {
      await dimensionsService.deleteDimension(dimensionId);
      commit('REMOVE_DIMENSION', dimensionId);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Dimension deleted successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to delete dimension',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Add result description to dimension
   */
  async addResultDescription({ commit, dispatch }, { dimensionId, resultDescription }) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.addResultDescription(dimensionId, resultDescription);
      commit('SET_DIMENSION', response.data);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Result description added successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to add result description',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Remove result description from dimension
   */
  async removeResultDescription({ commit, dispatch }, { dimensionId, index }) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.removeResultDescription(dimensionId, index);
      commit('SET_DIMENSION', response.data);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Result description removed successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to remove result description',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Search dimensions
   */
  async searchDimensions({ commit, dispatch }, searchTerm) {
    commit('SET_LOADING', true);
    try {
      const dimensions = await dimensionsService.searchDimensions(searchTerm);
      commit('SET_DIMENSIONS', dimensions);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to search dimensions',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Clear error
   */
  clearError({ commit }) {
    commit('SET_ERROR', null);
  },

  /**
   * Set current dimension for editing
   */
  setCurrentDimension({ commit }, dimension) {
    commit('SET_CURRENT_DIMENSION', dimension);
  },

  /**
   * Clear current dimension
   */
  clearCurrentDimension({ commit }) {
    commit('CLEAR_CURRENT_DIMENSION');
  },

  /**
   * Add question to dimension
   */
  async addDimensionQuestion({ commit, dispatch }, { dimensionId, questionData }) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.addDimensionQuestion(dimensionId, questionData);
      commit('SET_DIMENSION', response.data);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Question added successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to add question',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update question in dimension
   */
  async updateDimensionQuestion(
    { commit, dispatch },
    { dimensionId, questionIndex, questionData },
  ) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.updateDimensionQuestion(
        dimensionId,
        questionIndex,
        questionData,
      );
      commit('SET_DIMENSION', response.data);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Question updated successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to update question',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete question from dimension
   */
  async deleteDimensionQuestion({ commit, dispatch }, { dimensionId, questionIndex }) {
    commit('SET_LOADING', true);
    try {
      const response = await dimensionsService.removeDimensionQuestion(dimensionId, questionIndex);
      commit('SET_DIMENSION', response.data);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Question deleted successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to delete question',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Import-related actions
  /**
   * Analyze Excel import data for conflicts
   */
  async analyzeImportConflicts({ commit, dispatch }, { excelData, existingDimensions }) {
    commit('SET_IMPORT_STATUS', 'analyzing');
    try {
      // Import the DimensionMatchingService dynamically to avoid circular dependencies
      const { default: DimensionMatchingService } = await import('@/services/DimensionMatchingService');

      const analysis = DimensionMatchingService.analyzeImport(excelData, existingDimensions);

      commit('SET_IMPORT_ANALYSIS', analysis);
      commit('SET_IMPORT_STATUS', 'idle');

      return analysis;
    } catch (error) {
      commit('SET_IMPORT_STATUS', 'error');
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to analyze import data',
          severity: 'error',
        },
        { root: true },
      );
      throw error;
    }
  },

  /**
   * Execute import based on user choice
   */
  async executeImportWithChoice({ commit, dispatch }, { userChoice }) {
    commit('SET_IMPORT_STATUS', 'importing');

    try {
      const { analysis } = state.importState;
      if (!analysis) {
        throw new Error('No import analysis available');
      }

      const importResult = {
        imported: [],
        skipped: [],
        overridden: [],
        timestamp: new Date().toISOString(),
      };

      // Always import new dimensions
      const newDimensionResults = await Promise.allSettled(
        analysis.newDimensions.map((newDimension) => dimensionsService
          .createDimension(newDimension)),
      );

      newDimensionResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          importResult.imported.push(result.value.data);
        } else {
          console.error('Failed to import new dimension:', result.reason);
        }
      });

      // Handle conflicts based on user choice
      if (userChoice === 'override' && analysis.conflicts.length > 0) {
        const conflictResults = await Promise.allSettled(
          analysis.conflicts.map(async (conflict) => {
            // Remove _id and __v for update
            const dimensionToUpdate = { ...conflict.newDimension };
            delete dimensionToUpdate._id;
            delete dimensionToUpdate.__v;

            return dimensionsService.updateDimension(
              conflict.existing._id,
              dimensionToUpdate,
            );
          }),
        );

        conflictResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            importResult.overridden.push(result.value.data);
          } else {
            console.error('Failed to override dimension:', result.reason);
          }
        });
      } else {
        // Skip conflicts
        importResult.skipped = analysis.conflicts.map((c) => c.existing);
      }

      // Store result for rollback
      commit('SET_LAST_IMPORT_RESULT', importResult);
      commit('SET_CAN_ROLLBACK', true);
      commit('SET_IMPORT_STATUS', 'success');

      // Refresh dimensions list
      dispatch('fetchDimensions');

      // Show success message
      const totalImported = importResult.imported.length + importResult.overridden.length;
      dispatch(
        'notifications/showToast',
        {
          detail: `Import completed: ${totalImported} dimensions processed`,
          severity: 'success',
        },
        { root: true },
      );

      return importResult;
    } catch (error) {
      commit('SET_IMPORT_STATUS', 'error');
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Import failed',
          severity: 'error',
        },
        { root: true },
      );
      throw error;
    }
  },

  /**
   * Rollback last import operation
   */
  async rollbackLastImport({ commit, dispatch, state: storeState }) {
    const { lastImportResult } = storeState.importState;
    if (!lastImportResult) {
      throw new Error('No import operation to rollback');
    }

    commit('SET_IMPORT_STATUS', 'importing');

    try {
      // Rollback imported dimensions
      const deleteResults = await Promise.allSettled(
        lastImportResult.imported.map((importedDimension) =>
          dimensionsService.deleteDimension(importedDimension._id)),
      );

      deleteResults.forEach((result) => {
        if (result.status === 'rejected') {
          console.error('Failed to rollback imported dimension:', result.reason);
        }
      });

      // Rollback overridden dimensions (restore original data)
      const restoreResults = await Promise.allSettled(
        lastImportResult.overridden.map(async (overriddenDimension) => {
          // Find original dimension data from conflicts
          const { analysis } = storeState.importState;
          const conflict = analysis.conflicts.find((c) =>
            c.newDimension.value === overriddenDimension.value);

          if (conflict) {
            const originalData = { ...conflict.existing };
            delete originalData._id;
            delete originalData.__v;

            return dimensionsService.updateDimension(overriddenDimension._id, originalData);
          }
          return null;
        }),
      );

      restoreResults.forEach((result) => {
        if (result.status === 'rejected') {
          console.error('Failed to rollback overridden dimension:', result.reason);
        }
      });

      // Clear rollback data
      commit('SET_LAST_IMPORT_RESULT', null);
      commit('SET_CAN_ROLLBACK', false);
      commit('SET_IMPORT_STATUS', 'idle');

      // Refresh dimensions list
      dispatch('fetchDimensions');

      dispatch(
        'notifications/showToast',
        {
          detail: 'Rollback completed successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_IMPORT_STATUS', 'error');
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Rollback failed',
          severity: 'error',
        },
        { root: true },
      );
      throw error;
    }
  },

  /**
   * Clear import state and analysis
   */
  clearImportState({ commit }) {
    commit('CLEAR_IMPORT_STATE');
  },
};

const mutations = {
  /**
   * Set all dimensions
   */
  SET_DIMENSIONS(storeState, dimensions) {
    // eslint-disable-next-line no-param-reassign
    storeState.dimensions = dimensions;
  },

  /**
   * Set single dimension (add or update)
   */
  SET_DIMENSION(storeState, dimension) {
    const existingDimensionIndex = storeState.dimensions.findIndex((d) => d._id === dimension._id);

    if (existingDimensionIndex !== -1) {
      console.log('updating dimension ...');
      // Update existing dimension
      // eslint-disable-next-line no-param-reassign
      storeState.dimensions.splice(existingDimensionIndex, 1, dimension);
    } else {
      console.log('adding dimension ...');
      // Add new dimension
      // eslint-disable-next-line no-param-reassign
      storeState.dimensions.push(dimension);
    }

    // Also update currentDimension if it's the same dimension
    if (storeState.currentDimension && storeState.currentDimension._id === dimension._id) {
      // eslint-disable-next-line no-param-reassign
      storeState.currentDimension = dimension;
    }
  },

  /**
   * Remove dimension
   */
  REMOVE_DIMENSION(storeState, dimensionId) {
    // eslint-disable-next-line no-param-reassign
    storeState.dimensions = storeState.dimensions.filter(
      (dimension) => dimension._id !== dimensionId,
    );
  },

  /**
   * Set loading state
   */
  SET_LOADING(storeState, loading) {
    // eslint-disable-next-line no-param-reassign
    storeState.loading = loading;
  },

  /**
   * Set error state
   */
  SET_ERROR(storeState, error) {
    // eslint-disable-next-line no-param-reassign
    storeState.error = error;
  },

  /**
   * Set current dimension for editing
   */
  SET_CURRENT_DIMENSION(storeState, dimension) {
    console.log('SET_CURRENT_DIMENSION mutation called with:', dimension);
    console.log('Questions in mutation:', dimension?.questions);
    console.log('Questions length in mutation:', dimension?.questions?.length);
    // eslint-disable-next-line no-param-reassign
    storeState.currentDimension = dimension;
    console.log('Store state currentDimension after mutation:', storeState.currentDimension);
  },

  /**
   * Clear current dimension
   */
  CLEAR_CURRENT_DIMENSION(storeState) {
    // eslint-disable-next-line no-param-reassign
    storeState.currentDimension = null;
  },

  // Import-related mutations
  /**
   * Set import analysis
   */
  SET_IMPORT_ANALYSIS(storeState, analysis) {
    // eslint-disable-next-line no-param-reassign
    storeState.importState.analysis = analysis;
  },

  /**
   * Set import status
   */
  SET_IMPORT_STATUS(storeState, status) {
    // eslint-disable-next-line no-param-reassign
    storeState.importState.status = status;
  },

  /**
   * Set last import result for rollback
   */
  SET_LAST_IMPORT_RESULT(storeState, result) {
    // eslint-disable-next-line no-param-reassign
    storeState.importState.lastImportResult = result;
  },

  /**
   * Set rollback availability
   */
  SET_CAN_ROLLBACK(storeState, canRollback) {
    // eslint-disable-next-line no-param-reassign
    storeState.importState.canRollback = canRollback;
  },

  /**
   * Clear all import state
   */
  CLEAR_IMPORT_STATE(storeState) {
    // eslint-disable-next-line no-param-reassign
    storeState.importState.analysis = null;
    // eslint-disable-next-line no-param-reassign
    storeState.importState.status = 'idle';
    // eslint-disable-next-line no-param-reassign
    storeState.importState.lastImportResult = null;
    // eslint-disable-next-line no-param-reassign
    storeState.importState.canRollback = false;
    // eslint-disable-next-line no-param-reassign
    storeState.importState.rollbackData = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
